import { Button, Col, Divider, Row, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AisleContext from "../../../../context/wms/aisle/context";
import AisleContextProvider from "../../../../context/wms/aisle/provider";
import { ICreateAisle } from "../../../../models/wms/aisle/request";
import { AisleLayout } from "../../../../models/wms/enums";
import EndPoints from "../../../../services/end-points";
import { execute } from "../../../../utils/helpers/execute";
import FieldBuilder from "../../../form-components/field-builder";
import Spin from "../../../general/antd/spin";
import styles from "./style.module.scss";
import useHierarchicalDropdown, { ChainItem } from "../../../../hooks/wms/use-hierarchical-dropdown";
import PreviousChainDisplay from "../../shared/previous-chain-display";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(AisleContext);
  const { id } = useParams();

  const methods = useForm<ICreateAisle>({
    defaultValues: {
      zoneId: details?.zoneId || undefined,
      sides: details?.sides || undefined,
      pickSequence: details?.pickSequence || undefined,
      isActive: details?.isActive || false,
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
  } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    visibleLevels,
    previousChain,
    capturePreviousChain,
    clearPreviousChain,
    resetFromLevel,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    initializeForEdit,
  } = useHierarchicalDropdown({ debounceMs: 300 });

  // Watch form values for ancestor selectors
  const formStoreId = watch("storeId" as any);
  const formZoneTypeId = watch("zoneTypeId" as any);

  // Sync form values with hook selected values
  useEffect(() => {
    if (formStoreId !== selectedStoreId) {
      setSelectedStoreId(formStoreId);
    }
  }, [formStoreId, setSelectedStoreId]);

  useEffect(() => {
    if (formZoneTypeId !== selectedZoneTypeId) {
      setSelectedZoneTypeId(formZoneTypeId);
    }
  }, [formZoneTypeId, setSelectedZoneTypeId]);

  // Capture previous chain on mount in edit mode
  useEffect(() => {
    if (id && details) {
      const chain: ChainItem[] = [];
      if (details.store?.name) {
        chain.push({ label: details.store.name, value: details.storeId });
      } else if (details.storeId) {
        chain.push({ label: `Store ${details.storeId}`, value: details.storeId });
      }
      if (details.zone?.name) {
        chain.push({ label: details.zone.name, value: details.zoneId });
      } else if (details.zoneId) {
        chain.push({ label: `Zone ${details.zoneId}`, value: details.zoneId });
      }
      if (chain.length > 0) {
        capturePreviousChain(chain);
      }
    }
  }, [id, details]);

  // Handle store change - reset children
  const handleStoreChange = (storeId: number | undefined) => {
    if (storeId && storeId !== details?.storeId) {
      resetFromLevel('store');
      setValue('zoneId', undefined as any);
    }
  };

  // Handle zone type change - reset children
  const handleZoneTypeChange = (zoneTypeId: number | undefined) => {
    if (zoneTypeId && zoneTypeId !== details?.zone?.zoneTypeId) {
      resetFromLevel('zone');
      setValue('zoneId', undefined as any);
    }
  };

  // When details load, reset form and initialize hierarchical dropdowns immediately
  useEffect(() => {
    if (details) {
      reset({
        storeId: details.storeId ?? undefined,
        zoneTypeId: details.zone?.zoneTypeId ?? undefined,
        zoneId: details.zoneId ?? undefined,
        sides: details.sides ?? undefined,
        pickSequence: details.pickSequence ?? undefined,
        isActive: details.isActive ?? false,
      } as any);
      initializeForEdit({
        storeId: details.storeId,
        zoneTypeId: details.zone?.zoneTypeId,
      });
    }
  }, [details, reset, initializeForEdit]);

  const normalizePickSequence = (v: unknown): number | undefined => {
    if (v === "" || v === undefined || v === null) return undefined;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n) || n < 1) return undefined;
    return n;
  };

  const onSubmit = async (data: ICreateAisle) => {
    const { storeId, zoneTypeId, pickSequence, ...rest } = data as any;
    const pickSeq = normalizePickSequence(pickSequence);
    const submitData = {
      ...rest,
      ...(pickSeq !== undefined ? { pickSequence: pickSeq } : {}),
    };
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.wmsLayout.updateAisle(details.id, submitData);
        } else {
          await EndPoints.wmsLayout.createAisle(submitData);
        }
        navigate("/wms/aisles");
      },
      fallback: (error) => {
        message.error(t('operation_failed'));
      },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_aisle") : t("add_aisle")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <>
              {previousChain && (
                <PreviousChainDisplay
                  chain={previousChain}
                  onClear={clearPreviousChain}
                />
              )}
              <div className={styles.children}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="storeId"
                      label={t("select_store")}
                      input={{
                        type: "select",
                        loading: loadingStores,
                        allowSearch: true,
                        options: stores.map((s) => ({
                          label: s.label,
                          value: s.id,
                        })),
                        onChange: handleStoreChange,
                      }}
                      rules={{ required: !details }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="zoneTypeId"
                      label={t("zone_type")}
                      input={{
                        type: "select",
                        loading: loadingZoneTypes,
                        allowSearch: true,
                        options: zoneTypes.map((zt) => ({
                          label: zt.label,
                          value: zt.id,
                        })),
                        onChange: handleZoneTypeChange,
                      }}
                      rules={{ required: false }}
                    />
                  </Col>
                  {visibleLevels.zone && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="zoneId"
                        label={t("select_zone")}
                        input={{
                          type: "select",
                          loading: loadingZones,
                          allowSearch: true,
                          options: zones.map((z) => ({
                            label: z.label,
                            value: z.id,
                          })),
                        }}
                        rules={{ required: true }}
                      />
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="sides"
                      label={t("available_sides")}
                      input={{
                        type: "select",
                        options: [
                          { label: t("left_side"), value: AisleLayout.LEFT },
                          { label: t("right_side"), value: AisleLayout.RIGHT },
                          { label: t("both_sides"), value: AisleLayout.BOTH },
                        ],
                      }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="pickSequence"
                      label={t("pick_sequence")}
                      input={{ type: "number" }}
                      rules={{
                        required: false,
                        validate: (v) =>
                          v === "" || v === undefined || v === null || Number(v) >= 1
                            ? true
                            : t("min", { input: t("pick_sequence"), value: 1 }),
                      }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="isActive"
                      label={t("is_active")}
                      input={{ type: "switch" }}
                      rules={{ required: false }}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const AisleForm = () => {
  return (
    <AisleContextProvider>
      <Form />
    </AisleContextProvider>
  );
};

export default AisleForm;
